import { useState } from 'react'
import { useLocation } from 'wouter'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ChevronLeft, Loader2, Eye, EyeOff, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FloatingInput } from '@/components/ui/floating-input'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/hooks/use-user'
import { apiFetch } from '@/utils'

const passwordSchema = z.object({
	newPassword: z.string().min(8, 'Password must be at least 8 characters'),
	confirmPassword: z.string().min(1, 'Please confirm your password')
}).refine((data) => data.newPassword === data.confirmPassword, {
	message: "Passwords don't match",
	path: ['confirmPassword']
})

type PasswordFormData = z.infer<typeof passwordSchema>

interface PasswordRequirement {
	label: string
	test: (password: string) => boolean
}

const passwordRequirements: PasswordRequirement[] = [
	{ label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
	{ label: 'Contains uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
	{ label: 'Contains lowercase letter', test: (pw) => /[a-z]/.test(pw) },
	{ label: 'Contains a number', test: (pw) => /\d/.test(pw) },
	{ label: 'Contains special character', test: (pw) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw) },
]

export default function ChangePasswordPage() {
	const [, navigate] = useLocation()
	const { toast } = useToast()
	const { uid } = useUser()
	const [isUpdating, setIsUpdating] = useState(false)
	const [showPassword, setShowPassword] = useState({
		new: false,
		confirm: false,
	})

	const form = useForm<PasswordFormData>({
		resolver: zodResolver(passwordSchema),
		defaultValues: {
			newPassword: '',
			confirmPassword: ''
		}
	})

	const watchedPassword = form.watch('newPassword')
	const watchedConfirm = form.watch('confirmPassword')

	const togglePassword = (key: 'new' | 'confirm') => {
		setShowPassword((prev) => ({
			...prev,
			[key]: !prev[key],
		}))
	}

	const onSubmit = async (data: PasswordFormData) => {
		setIsUpdating(true)
		try {
			const response = await apiFetch(`/v1/users/${uid}`, {
				method: 'PATCH',
				body: JSON.stringify({
					password: data.newPassword
				}),
				withCredentials: true
			})

			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.error?.message || 'Failed to update password')
			}

			toast({
				title: 'Password Updated',
				description: 'Your password has been successfully changed.'
			})
			navigate('/account')
		} catch (error) {
			toast({
				title: 'Update Failed',
				description: error instanceof Error ? error.message : 'Failed to update password',
				variant: 'destructive'
			})
		} finally {
			setIsUpdating(false)
		}
	}

	const passwordsMatch = watchedPassword.length > 0 && watchedConfirm.length > 0 && watchedPassword === watchedConfirm

	return (
		<div className="h-[100dvh] bg-[#18181B] text-white flex flex-col">
			<div className="max-w-5xl w-full mx-auto flex flex-col flex-1">
				{/* Header */}
				<div
					role="banner"
					className="sticky top-0 z-50 bg-[#18181B] flex items-center justify-start gap-2 text-white py-3 px-5"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/account')}
						className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
					</button>
					<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
						Change Password
					</h1>
				</div>

				{/* Center content */}
				<div className="flex-1 flex items-center justify-center px-5 pb-20">
					<div className="w-full max-w-sm space-y-5">
						{/* New Password */}
						<div className="relative">
							<FloatingInput
								label="New Password"
								type={showPassword.new ? 'text' : 'password'}
								autoFocus
								className="pr-12"
								{...form.register('newPassword')}
							/>
							<button
								type="button"
								onClick={() => togglePassword('new')}
								className="absolute right-4 top-1/2 -translate-y-1/2 text-[#888888]"
							>
								{showPassword.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
							</button>
						</div>

						{/* Confirm Password */}
						<div className="relative">
							<FloatingInput
								label="Confirm Password"
								type={showPassword.confirm ? 'text' : 'password'}
								className="pr-12"
								{...form.register('confirmPassword')}
							/>
							<button
								type="button"
								onClick={() => togglePassword('confirm')}
								className="absolute right-4 top-1/2 -translate-y-1/2 text-[#888888]"
							>
								{showPassword.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
							</button>
						</div>

						{/* Passwords match indicator */}
						{watchedConfirm.length > 0 && (
							<div className="flex items-center gap-2 px-1">
								{passwordsMatch ? (
									<Check className="w-4 h-4 text-[#2ecc71] shrink-0" />
								) : (
									<X className="w-4 h-4 text-[#EF4444] shrink-0" />
								)}
								<span className={`text-xs font-figtree ${passwordsMatch ? 'text-[#2ecc71]' : 'text-[#EF4444]'}`}>
									{passwordsMatch ? 'Passwords match' : "Passwords don't match"}
								</span>
							</div>
						)}

						<Button
							onClick={form.handleSubmit(onSubmit)}
							disabled={isUpdating}
							className="w-full h-[42px] bg-[#EF4444] font-figtree font-semibold leading-[14px] rounded-[26px] text-white text-sm shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96]"
						>
							{isUpdating ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Updating...
								</>
							) : (
								'Change Password'
							)}
						</Button>

						{/* Password Requirements - below button */}
						<div className="space-y-2 px-1">
							{passwordRequirements.map((req) => {
								const hasInput = watchedPassword.length > 0
								const met = hasInput && req.test(watchedPassword)
								return (
									<div key={req.label} className="flex items-center gap-2">
										{hasInput ? (
											met ? (
												<Check className="w-4 h-4 text-[#2ecc71] shrink-0" />
											) : (
												<X className="w-4 h-4 text-[#EF4444] shrink-0" />
											)
										) : (
											<div className="w-4 h-4 rounded-full border border-[#4C4C4C] shrink-0" />
										)}
										<span className={`text-xs font-figtree ${
											!hasInput ? 'text-[#888888]' : met ? 'text-[#2ecc71]' : 'text-[#888888]'
										}`}>
											{req.label}
										</span>
									</div>
								)
							})}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
